import { PublicLayout } from "@/components/PublicLayout";
import { SEOHead } from "@/components/SEOHead";
import { motion } from "framer-motion";
import { ArrowRight, ExternalLink, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import headshot from "@/assets/david-isei-headshot.jpg";

const fade = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" } as any,
  transition: { duration: 0.6 },
};

const roles = [
  "Executive Director, Hawaiʻi Healthcare Task Force",
  "Architect, Hawaiʻi Healthcare Intelligence Platform",
  "CEO, Mohala Health",
  "Principal, ISEI Consulting LLC",
  "U.S. Air Force veteran",
];

export default function AboutPage() {
  return (
    <PublicLayout>
      <SEOHead
        title="About · David Isei"
        description="David Isei, MPH, is Executive Director of the Hawaiʻi Healthcare Task Force and architect of the Hawaiʻi Healthcare Intelligence Platform. How the work is done, how figures are labeled, and how corrections are published."
        keywords={[
          "David Isei",
          "Hawaii Healthcare Task Force",
          "Hawaii Healthcare Intelligence Platform",
          "Hawaii healthcare workforce",
          "network adequacy",
        ]}
        url="https://davidisei.com/about"
        type="profile"
      />

      {/* Header */}
      <section className="pt-20 pb-12 max-w-3xl mx-auto px-6">
        <motion.div {...fade}>
          <img
            src={headshot}
            alt="David Isei"
            className="mb-10 mx-auto block w-full max-w-[280px] h-auto rounded-[12px] border-[3px] border-[#C9A84C]"
            style={{ boxShadow: "0 4px 24px rgba(0,0,0,0.4)" }}
          />
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium mb-4">
            About
          </p>
          <h1 className="font-display text-4xl md:text-5xl font-bold text-foreground leading-[1.1] mb-3">
            About David
          </h1>
          <p className="text-[13px] font-mono text-accent/70 tracking-wide">
            MPH · MAOL · PMP · SPHR · LSSBB · CySA+
          </p>
        </motion.div>
      </section>

      {/* Bio */}
      <section className="py-16 lg:py-20 border-t border-border/40">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fade} className="space-y-6 text-lg leading-relaxed">
            <p className="text-foreground">
              David Isei, MPH, MAOL, PMP, SPHR, is Executive Director of the
              Hawaiʻi Healthcare Task Force, a clinician-led coalition, and the
              architect of the Hawaiʻi Healthcare Intelligence Platform at
              hawaiihip.org.
            </p>
            <p className="text-muted-foreground">
              The platform reconciles federal, state, and carrier sources into
              figures that carry a named authority and a retrieval date. Its
              work has measured the distance between physicians listed in
              insurer directories and physicians actually delivering care,
              quantified the gap between registered and practicing primary care
              clinicians on Hawaiʻi Island, and produced the first Hawaiʻi
              measurement of how travel distance affects mammography screening.
            </p>
            <p className="text-muted-foreground">
              Isei convened Hawaiʻi's Insurance Commissioner and the Director of
              Commerce and Consumer Affairs on network adequacy in July 2026. He
              is a U.S. Air Force veteran and mentors the AHEC
              CIRCLE student research cohort.
            </p>
          </motion.div>

          <motion.ul {...fade} className="mt-10 space-y-3">
            {roles.map((role) => (
              <li
                key={role}
                className="flex items-center gap-3 py-2 border-b border-border/30 last:border-0"
              >
                <span className="h-1.5 w-1.5 rounded-full bg-accent shrink-0" />
                <span className="text-foreground">{role}</span>
              </li>
            ))}
          </motion.ul>
        </div>
      </section>

      {/* How I work */}
      <section className="py-16 lg:py-20 bg-muted/30">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fade}>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-8">
              How I work
            </h2>
            <div className="space-y-5 text-lg text-muted-foreground leading-relaxed">
              <p>
                Every figure I publish is labeled one of four ways: measured,
                computed by us from record-level data; direct, published by a
                named authority and cited with a retrieval date; field-verified,
                reported by program leadership with corroboration pending; or
                modeled, a scenario that is never presented as measured.
              </p>
              <p>
                When a figure changes, I publish the change and the reason. In
                August 2026 that discipline cut one of our own headline findings
                by roughly a third. The number got smaller and the claim got
                harder to knock down.
              </p>
              <p>
                Limitations go on the page, not in a footnote. Ecological
                analyses describe communities, not individuals. Claims data have
                blind spots. Registry counts are ceilings and claims-based counts
                are floors.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Before this */}
      <section className="py-16 lg:py-20 border-t border-border/40">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fade}>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-8">
              Before this
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Fifteen years across state and federal service in health policy,
              workforce strategy, and federal grant management, and Hawaiʻi Air National Guard service in supply and logistics with
              the 297th Air Traffic Control Squadron, ending at Headquarters,
              Hawaiʻi Air National Guard.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Credentials + links */}
      <section className="py-16 lg:py-20 bg-muted/30">
        <div className="max-w-3xl mx-auto px-6">
          <motion.div {...fade}>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-8">
              Credentials
            </h2>
            <ul className="flex flex-wrap gap-3">
              {["MPH", "MAOL", "PMP", "SPHR", "LSSBB", "CySA+"].map((c) => (
                <li
                  key={c}
                  className="rounded-full border border-accent/40 px-3 py-1 font-mono text-[12px] text-accent"
                >
                  {c}
                </li>
              ))}
            </ul>

            <div className="mt-10 flex flex-wrap gap-5">
              <a
                href="https://www.linkedin.com/in/david-isei/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-accent hover:opacity-80"
              >
                <Linkedin className="h-4 w-4" /> LinkedIn
              </a>
              <a
                href="https://hawaiihip.org"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-sm font-semibold text-accent hover:opacity-80"
              >
                <ExternalLink className="h-4 w-4" /> hawaiihip.org
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Closing */}
      <section className="pt-20 pb-20 lg:pt-24 lg:pb-24 border-t border-border/40">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <motion.div {...fade}>
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-8">
              Read the record
            </h2>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                asChild
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 font-semibold px-8 h-12"
              >
                <Link to="/dashboard">
                  State of the System
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-accent text-accent hover:bg-accent/10 hover:text-accent font-semibold px-8 h-12 bg-transparent"
              >
                <Link to="/connect">Contact</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
}
